import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { ROLE_HOME, registerSchema, type RegisterInput, type SystemRole } from '@aurea/contracts';
import { ApiRequestError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { AuthScene } from './AuthScene';

/**
 * Регистрация соискателя по пригласительной ссылке: aurea.you/r/<token>.
 * Ссылку рассылают участникам Zoom-встречи, она живёт неделю.
 */
export function RegisterPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formError, setFormError] = useState<string | null>(null);

  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.checkInvite(token),
    retry: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    defaultValues: { token, lastName: '', firstName: '', email: '', password: '' },
  });

  if (user) {
    // Уводим в кабинет той роли, что есть у вошедшего. Раньше здесь всегда
    // стоял кабинет соискателя, и сотрудник, открывший свою же ссылку,
    // попадал на чужой экран с ошибкой загрузки.
    void navigate(ROLE_HOME[user.role.code as SystemRole] ?? ROLE_HOME.candidate, {
      replace: true,
    });
    return null;
  }

  if (invite.isPending) {
    return (
      <AuthScene>
        <p className="auth__stateText">Проверяем ссылку…</p>
      </AuthScene>
    );
  }

  if (invite.isError) {
    return <InviteProblem error={invite.error} />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.register({ ...values, token });
      // Сессия выдаётся сразу — второй раз вводить пароль незачем.
      window.location.assign(ROLE_HOME.candidate);
    } catch (error) {
      setFormError(
        error instanceof ApiRequestError
          ? error.message
          : 'Не получилось зарегистрироваться. Попробуйте ещё раз.',
      );
    }
  });

  return (
    <AuthScene wide>
      <h1 className="auth__title">Регистрация</h1>

      <form onSubmit={onSubmit} noValidate>
        {formError && (
          <div className="auth__note" role="alert">
            {formError}
          </div>
        )}

        <div className="auth__row">
          <label className="auth__field">
            <span className="auth__label">Фамилия</span>
            <input className="auth__input" autoComplete="family-name" {...register('lastName')} />
            {errors.lastName?.message && (
              <span className="auth__error">{errors.lastName.message}</span>
            )}
          </label>

          <label className="auth__field">
            <span className="auth__label">Имя</span>
            <input className="auth__input" autoComplete="given-name" {...register('firstName')} />
            {errors.firstName?.message && (
              <span className="auth__error">{errors.firstName.message}</span>
            )}
          </label>
        </div>

        <label className="auth__field">
          <span className="auth__label">Почта</span>
          <input
            className="auth__input"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            {...register('email')}
          />
          {errors.email?.message && <span className="auth__error">{errors.email.message}</span>}
        </label>

        <label className="auth__field">
          <span className="auth__label">Пароль</span>
          <input
            className="auth__input"
            type="password"
            autoComplete="new-password"
            {...register('password')}
          />
          {errors.password?.message && (
            <span className="auth__error">{errors.password.message}</span>
          )}
        </label>

        <button className="auth__submit" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Создаём кабинет…' : 'Зарегистрироваться'}
        </button>
      </form>
    </AuthScene>
  );
}

/**
 * Ссылка может быть просрочена, отозвана или исчерпана. На экране должно быть
 * написано, что именно произошло, — иначе человек не поймёт, что ему делать.
 */
function InviteProblem({ error }: { error: unknown }) {
  const code = error instanceof ApiRequestError ? error.code : 'INTERNAL';

  const messages: Record<string, { icon: string; title: string; text: string }> = {
    INVITE_EXPIRED: {
      icon: '⌛',
      title: 'Срок действия ссылки истёк',
      text: 'Ссылка действует неделю. Попросите рекрутёра прислать новую — это займёт минуту.',
    },
    INVITE_REVOKED: {
      icon: '🚫',
      title: 'Ссылка отозвана',
      text: 'Эта ссылка больше не действует. Напишите рекрутёру, он выдаст новую.',
    },
    INVITE_LIMIT_REACHED: {
      icon: '🙈',
      title: 'По ссылке уже зарегистрировались',
      text: 'Лимит регистраций исчерпан. Попросите рекрутёра прислать свежую ссылку.',
    },
    NOT_FOUND: {
      icon: '🔍',
      title: 'Ссылка не найдена',
      text: 'Проверьте, что адрес скопирован целиком, без потерянных символов в конце.',
    },
  };

  const view = messages[code] ?? {
    icon: '😕',
    title: 'Не получилось открыть ссылку',
    text: 'Попробуйте обновить страницу. Если не поможет — напишите рекрутёру.',
  };

  return (
    <AuthScene>
      <div className="auth__state">
        <div className="auth__stateIcon">{view.icon}</div>
        <h1 className="auth__stateTitle">{view.title}</h1>
        <p className="auth__stateText">{view.text}</p>
      </div>
    </AuthScene>
  );
}
